import { AppointmentRequest, AppointmentCheckInRequest } from "@common/models/requests";
import { ResourceApi } from '@fullcalendar/resource-common';
import { DateTime } from "luxon";
import { AppointmentFlowActionOperations, AppointmentStatus, EntityScheduleType, ReservationAssignmentStatuses } from "@common/models/Enums";
import { ISelectOption } from "@common/components/forms";
import { StringUtility } from "@common/utils";
import { defaultTo } from "lodash";
import { IModel, ModelRecord, PetAlertModel, IPetAlertModel, schedule } from "@common/models";
import { AppointmentStatusRequest } from "./requests/AppointmentStatusRequest";
import { ICheckInModel } from "@models/forms/ICheckInModel";

export interface IAppointmentListingModel extends IModel {
    clinicId: number
    clinicStoreId: number
    clinicLocationName: string
    reservationId: number
    rescheduledReservationId: number | undefined
    rescheduledToDate: string | undefined
    visitId?: number
    appointmentStatusId: AppointmentStatus
    clientId: number
    clientFirstName: string
    clientLastName: string
    clientEmail: string
    clientPhone: string | undefined
    petId: number
    petAlerts: IPetAlertModel[] | undefined
    petName: string
    speciesName: string | undefined
    breedName: string | undefined
    genderName: string | undefined
    petDOB: string | undefined
    veterinarianId?: number | undefined
    veterinarianTitle: string | undefined
    veterinarianFirstName: string | undefined
    veterinarianLastName: string | undefined
    hospitalScheduleTemplateId?: number | undefined
    hospitalScheduleTemplateName: string | undefined
    appointmentTypeId: number
    appointmentTypeName: string
    appointmentTypeColor: string
    visitCompletionTypeId: number | undefined
    visitCompletionTypeName: string | undefined

    startDate: string
    startTime: string
    endDate: string
    endTime: string
    createdDate: string
    lastModifiedDate: string

    visitStarted: string | undefined
    checkinStart: string | undefined
    inProgressStart: string | undefined
    admittedStart: string | undefined
    finalizedStart: string | undefined
    paidStart: string | undefined
    checkoutStart: string | undefined

    isDropOff: boolean
    isOnlineBooking: boolean
    reasonForVisit: string | undefined
    notes: string | undefined
    assignmentStatus: ReservationAssignmentStatuses | undefined
    room: string | undefined

    dispensingAuthUserId: number | undefined
    dispensingUserFirstName: string | undefined
    dispensingUserLastName: string | undefined

    wasPriorCustomer: boolean
    isWalkIn: boolean
    canCall: boolean,
    canSMS: boolean,
    isRebook: boolean,
    previousStartDate: string,
    previousEndDate: string;
	isLinkedToClientAndPet: boolean | undefined;
}

export const defaultApptListingModel: IAppointmentListingModel = {
    reservationId: 0,
    clinicId: 0,
    appointmentStatusId: AppointmentStatus.Booked,
    clinicStoreId: 0,
    clinicLocationName: "",
    clientId: 0,
    clientFirstName: "",
    clientLastName: "",
    clientEmail: "",
    clientPhone: undefined,
    petId: 0,
    petName: "",
    speciesName: "",
    breedName: "",
    genderName: "",
    petDOB: "",
    appointmentTypeId: 0,
    appointmentTypeName: "",
    appointmentTypeColor: "",
    visitCompletionTypeName: undefined,
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    createdDate: "",
    lastModifiedDate: "",
    visitStarted: undefined,
    checkinStart: undefined,
    inProgressStart: undefined,
    admittedStart: undefined,
    finalizedStart: undefined,
    paidStart: undefined,
    checkoutStart: undefined,
    isOnlineBooking: false,
    isDropOff: false,
    veterinarianId: undefined,
    veterinarianFirstName: undefined,
    veterinarianLastName: undefined,
    veterinarianTitle: undefined,
    hospitalScheduleTemplateId: undefined,
    hospitalScheduleTemplateName: undefined,
    assignmentStatus: ReservationAssignmentStatuses.Unassigned,
    room: undefined,
    rescheduledReservationId: undefined,
    rescheduledToDate: undefined,
    petAlerts: undefined,
    visitCompletionTypeId: undefined,
    reasonForVisit: undefined,
    notes: undefined,
    dispensingAuthUserId: undefined,
    dispensingUserFirstName: "",
    dispensingUserLastName: "",
    wasPriorCustomer: false,
    isWalkIn: false,
    canCall: true,
    canSMS: true,
    rowVersion: "",
    isRebook: false,
    previousEndDate: "",
    previousStartDate: "",
	isLinkedToClientAndPet: false
}

export class AppointmentListingModel implements ModelRecord<IAppointmentListingModel> {
    rowVersion: string
    clinicId: number
    clinicStoreId: number
    clinicLocationName: string
    reservationId: number
    rescheduledReservationId: number | undefined
    rescheduledToDate: DateTime | undefined
    visitId: number | undefined
    appointmentStatusId: AppointmentStatus
    clientId: number
    clientFirstName: string
    clientLastName: string
    clientEmail: string
    clientPhone: string | undefined
    petId: number
    petAlerts: IPetAlertModel[] | undefined
    speciesName: string | undefined
    breedName: string | undefined
    genderName: string | undefined
    petDOB: string | undefined
    petName: string
    veterinarianId: number | undefined
    veterinarianTitle: string | undefined
    veterinarianFirstName: string | undefined
    veterinarianLastName: string | undefined
    hospitalScheduleTemplateId: number | undefined
    hospitalScheduleTemplateName: string | undefined
    appointmentTypeId: number
    appointmentTypeName: string
    appointmentTypeColor: string
    visitCompletionTypeId: number | undefined
    visitCompletionTypeName: string | undefined
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    createdDate: string
    lastModifiedDate: string
    visitStarted: string | undefined
    checkinStart: string | undefined
    inProgressStart: string | undefined
    admittedStart: string | undefined
    finalizedStart: string | undefined
    paidStart: string | undefined
    checkoutStart: string | undefined
    isOnlineBooking: boolean
    isDropOff: boolean
    reasonForVisit: string | undefined
    notes: string | undefined
    assignmentStatus: ReservationAssignmentStatuses
    room: string | undefined
    dispensingAuthUserId: number | undefined
    dispensingUserFirstName: string | undefined
    dispensingUserLastName: string | undefined
    wasPriorCustomer: boolean
    isWalkIn: boolean
    canCall: boolean
    canSMS: boolean
    isRebook: boolean
    previousStartDate: string
    previousEndDate: string
	isLinkedToClientAndPet: boolean

    constructor(params: IAppointmentListingModel) {
        this.rowVersion = defaultTo(params?.rowVersion, '');
        this.clinicId = defaultTo(params?.clinicId, 0);
        this.clinicStoreId = defaultTo(params?.clinicStoreId, 0);
        this.clinicLocationName = defaultTo(params?.clinicLocationName, '');
        this.reservationId = defaultTo(params?.reservationId, 0);
        this.rescheduledReservationId = defaultTo(params?.rescheduledReservationId, undefined);
        this.rescheduledToDate = StringUtility.SanitizeDateTimeISO(params.rescheduledToDate);
        this.visitId = defaultTo(params?.visitId, undefined);
        this.appointmentStatusId = defaultTo(params?.appointmentStatusId, AppointmentStatus.Booked);
        this.clientId = defaultTo(params?.clientId, 0);
        this.clientFirstName = defaultTo(params?.clientFirstName, '');
        this.clientLastName = defaultTo(params?.clientLastName, '');
        this.clientEmail = defaultTo(params?.clientEmail, '');
        this.clientPhone = defaultTo(params?.clientPhone, '');
        this.petId = defaultTo(params?.petId, 0);
        this.petAlerts = params?.petAlerts ? params?.petAlerts.map(p => new PetAlertModel(p)) : [] as PetAlertModel[];
        this.petName = defaultTo(params?.petName, '');
        this.speciesName = defaultTo(params?.speciesName, '');
        this.breedName = defaultTo(params?.breedName, '');
        this.genderName = defaultTo(params?.genderName, '');
        this.petDOB = defaultTo(params?.petDOB, '');
        this.assignmentStatus = defaultTo(params?.assignmentStatus, ReservationAssignmentStatuses.Unassigned);
        this.veterinarianId = defaultTo(params?.veterinarianId, undefined);
        this.veterinarianTitle = defaultTo(params?.veterinarianTitle, '');
        this.veterinarianFirstName = defaultTo(params?.veterinarianFirstName, '');
        this.veterinarianLastName = defaultTo(params?.veterinarianLastName, '');
        this.hospitalScheduleTemplateId = defaultTo(params?.hospitalScheduleTemplateId, undefined);
        this.hospitalScheduleTemplateName = defaultTo(params?.hospitalScheduleTemplateName, '');
        this.appointmentTypeId = defaultTo(params?.appointmentTypeId, 0);
        this.appointmentTypeName = defaultTo(params?.appointmentTypeName, '');
        this.appointmentTypeColor = defaultTo(params?.appointmentTypeColor, '');
        this.visitCompletionTypeId = defaultTo(params?.visitCompletionTypeId, undefined);
        this.visitCompletionTypeName = defaultTo(params?.visitCompletionTypeName, '');
        this.startDate = defaultTo(params?.startDate, '');
        this.endDate = defaultTo(params?.endDate, '');
        this.startTime = defaultTo(params?.startTime, '');
        this.endTime = defaultTo(params?.endTime, '');
        this.createdDate = defaultTo(params?.createdDate, '');
        this.lastModifiedDate = defaultTo(params?.lastModifiedDate, '');
        this.visitStarted = defaultTo(params?.visitStarted, undefined);
        this.checkinStart = defaultTo(params?.checkinStart, undefined);
        this.inProgressStart = defaultTo(params?.inProgressStart, undefined);
        this.admittedStart = defaultTo(params?.admittedStart, undefined);
        this.finalizedStart = defaultTo(params?.finalizedStart, undefined);
        this.paidStart = defaultTo(params?.paidStart, undefined);
        this.checkoutStart = defaultTo(params?.checkoutStart, undefined);
        this.isOnlineBooking = defaultTo(params?.isOnlineBooking, false);
        this.isDropOff = defaultTo(params?.isDropOff, false);
        this.reasonForVisit = defaultTo(params?.reasonForVisit, '');
        this.notes = defaultTo(params?.notes, '');
        this.room = StringUtility.SanitizeString(defaultTo(params?.room, undefined));
        this.dispensingAuthUserId = defaultTo(params?.dispensingAuthUserId, undefined);
        this.dispensingUserFirstName = defaultTo(params?.dispensingUserFirstName, '');
        this.dispensingUserLastName = defaultTo(params?.dispensingUserLastName, '');
        this.wasPriorCustomer = defaultTo(params?.wasPriorCustomer, false);
        this.isWalkIn = defaultTo(params?.isWalkIn, false);
        this.canCall = params.canCall;
        this.canSMS = params.canSMS;
        this.isRebook = params.isRebook
        this.previousStartDate = params.previousStartDate,
        this.previousEndDate = params.previousEndDate,
		this.isLinkedToClientAndPet = params.isLinkedToClientAndPet ?? false;
    }

    getAssignmentOrDrName() {
        if (!this.assignmentStatus) return 'Unassigned';
        switch (this.assignmentStatus) {
            case ReservationAssignmentStatuses.Assigned:
                return `Dr. ${this.veterinarianFirstName} ${this.veterinarianLastName}`;
            case ReservationAssignmentStatuses.Unassigned:
                return 'Unassigned';
            case ReservationAssignmentStatuses.Unassigned_Technician:
                if (this.veterinarianId && this.veterinarianId > 0)
                    return `Dr. ${this.veterinarianFirstName} ${this.veterinarianLastName}`;
                else
                    return 'Unassigned Technician';
            case ReservationAssignmentStatuses.Assigned_Template:
                return this.hospitalScheduleTemplateName;
            case ReservationAssignmentStatuses.Assigned_Technician:
                return `Attending Doctor: Dr. ${this.veterinarianFirstName} ${this.veterinarianLastName}`;
            default:
                throw 'Unknown assignment status';
        }
    }

    getVetTechOrAssistantName() {
        if (!this.dispensingAuthUserId)
            return 'Unassigned';
        else
            return `Vet Tech/Vet Assistant: ${this.dispensingUserFirstName} ${this.dispensingUserLastName}`
    }

    unassignedCheckInCanBeReassigned(operation: AppointmentFlowActionOperations): boolean {
        if ((this.assignmentStatus === ReservationAssignmentStatuses.Unassigned
            || this.assignmentStatus === ReservationAssignmentStatuses.Unassigned_Technician
            || this.assignmentStatus === ReservationAssignmentStatuses.Assigned_Template)
            && operation == AppointmentFlowActionOperations.CheckIn) {
            return true;
        }
        return false;
    }

    isFullyAssigned(): boolean { // helper function to ensure an appointment is filled out. external import may need these added.
        return (this.clientId !== 0 && this.petId !== 0);
    }

    static getDefault() {
        return new AppointmentListingModel(defaultApptListingModel);
    }

    static fromCalendarResource = (resource: ResourceApi | undefined, selectedDate: DateTime | null): AppointmentListingModel => {
        let apptModel: AppointmentListingModel = AppointmentListingModel.getDefault();
        apptModel.startDate = selectedDate ? selectedDate.toISO() : apptModel.startDate;

        const scheduleEntity = (resource?.extendedProps["scheduleEntity"] as schedule.HospitalScheduleDayEntityModel | undefined);
        const vet: schedule.HospitalScheduleVeterinarianModel | undefined = resource?.extendedProps["veterinarian"] as schedule.HospitalScheduleVeterinarianModel;

        if (scheduleEntity == null)
            throw 'invalid scheduleEntity, not expecting null';

        if (resource) {
            apptModel.assignmentStatus = resource.id === "-1" ? ReservationAssignmentStatuses.Unassigned_Technician : resource.id === "0" ? ReservationAssignmentStatuses.Unassigned : ReservationAssignmentStatuses.Assigned;

            switch (scheduleEntity.entityType) {
                case EntityScheduleType.Template:
                    apptModel.assignmentStatus = ReservationAssignmentStatuses.Assigned_Template;
                    apptModel.veterinarianFirstName = undefined;
                    apptModel.veterinarianLastName = undefined;
                    apptModel.veterinarianTitle = undefined;
                    apptModel.veterinarianId = undefined;
                    apptModel.hospitalScheduleTemplateId = scheduleEntity.entityId;
                    apptModel.hospitalScheduleTemplateName = scheduleEntity.entityTitle;
                    break;
                case EntityScheduleType.Veterinarian:
                    apptModel.assignmentStatus = ReservationAssignmentStatuses.Assigned;
                    apptModel.veterinarianFirstName = vet?.firstName ?? '';
                    apptModel.veterinarianLastName = vet?.lastName ?? '';
                    apptModel.veterinarianTitle = vet?.title;
                    apptModel.veterinarianId = scheduleEntity.entityId;
                    apptModel.hospitalScheduleTemplateId = undefined;
                    apptModel.hospitalScheduleTemplateName = undefined;
                    break;
                case EntityScheduleType.Unassigned:
                    apptModel.assignmentStatus = ReservationAssignmentStatuses.Unassigned;
                    apptModel.veterinarianFirstName = undefined;
                    apptModel.veterinarianLastName = undefined;
                    apptModel.veterinarianTitle = undefined;
                    apptModel.veterinarianId = undefined;
                    apptModel.hospitalScheduleTemplateId = undefined;
                    apptModel.hospitalScheduleTemplateName = undefined;
                    break;
                case EntityScheduleType.Technician:
                    apptModel.assignmentStatus = ReservationAssignmentStatuses.Unassigned_Technician;
                    apptModel.veterinarianFirstName = undefined;
                    apptModel.veterinarianLastName = undefined;
                    apptModel.veterinarianTitle = undefined;
                    apptModel.veterinarianId = undefined;
                    apptModel.hospitalScheduleTemplateId = undefined;
                    apptModel.hospitalScheduleTemplateName = undefined;
                    break;
                default:
                    throw 'unknown EntityScheduleType';
            }
        }

        return apptModel;
    }

    toRequest(hospitalId: number): AppointmentRequest {
        return AppointmentRequest.FromListingModel(hospitalId, this);
    }

    toCheckInRequest(checkInModel: ICheckInModel, createAsInProgress: boolean = false): AppointmentCheckInRequest {
        return AppointmentCheckInRequest.FromListingModel(this, checkInModel, createAsInProgress);
    }

    toStatusRequest(): AppointmentStatusRequest {
        return AppointmentStatusRequest.FromListingModel(this);
    }

    isBlockedFromReschedule(): boolean {
        return !_.includes([
            AppointmentStatus.Cancelled,
            AppointmentStatus.Rescheduled,
            AppointmentStatus.Check_In,
            AppointmentStatus.In_Progress,
            AppointmentStatus.Admitted,
            AppointmentStatus.Finalized,
            AppointmentStatus.Paid,
            AppointmentStatus.Check_Out,
            AppointmentStatus.NoShow
        ], this.appointmentStatusId);
    }

    shouldHide(): boolean {
        return _.includes([AppointmentStatus.Cancelled, AppointmentStatus.Rescheduled], this.appointmentStatusId);
    }
}
/**
 * All available appointment statuses and their matching id
 */
export const AppointmentStatusOptions: ISelectOption[] = [
    { value: 1, label: "Booked" },
    { value: 2, label: "Confirmed" },
    { value: 3, label: "Cancelled" },
    { value: 4, label: "Rescheduled" },
    { value: 5, label: "Showed" },
    { value: 7, label: "Clinic Cancelled" },
    { value: 8, label: "No Show" },
    { value: 9, label: "In Progress" },
    { value: 10, label: "Admitted" },
    { value: 11, label: "Finalized" },
    { value: 12, label: "Paid" },
    { value: 500, label: "Check In" },
    { value: 501, label: "Consultation" },
    { value: 502, label: "Vet Services" },
    { value: 503, label: "Check Out" },
    { value: 9999, label: "Walk-in" }, // UI only Appointment Status that maps to "Showed" with a Reservation Trait of: Reservation - Walk In
]
